import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Mail, Phone, MapPin, Facebook, Instagram, MessageCircle } from "lucide-react";

export default function Contacto() {
  const { tr } = useLang();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTimeout(() => {
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    }, 800);
  };

  const waMessage = encodeURIComponent("Hola, tengo una consulta sobre sus servicios.");

  return (
    <div className="bg-background text-foreground overflow-x-hidden selection:bg-primary/30 min-h-[100dvh]">

      {/* Hero */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/assets/hero-abstract.png" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(150deg, #0d2137 0%, #0a0f1e 55%, #060c18 100%)" }} />
          <div className="absolute inset-0 dark-texture" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-6 text-white">
              {tr.contact.title}
            </h1>
            <p className="text-xl text-slate-300 font-light max-w-2xl mx-auto">
              {tr.contact.desc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* WhatsApp CTA Banner */}
      <section className="py-16 px-6" style={{ background: "linear-gradient(135deg, #061e30 0%, #0a0f1e 60%, #0a1628 100%)" }}>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-between gap-8 p-8 rounded-3xl border border-primary/20 bg-white/5 backdrop-blur-sm"
          >
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#25D366" }}>
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">
                  {tr.contact.whatsappBannerTitle}
                </h3>
                <p className="text-slate-300 font-light leading-relaxed max-w-md">
                  {tr.contact.whatsappBannerDesc}
                </p>
              </div>
            </div>
            <a
              href={`https://wa.me/18097091552?text=${waMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-3 h-14 px-7 rounded-2xl font-bold text-white text-sm transition-all hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] hover:scale-105"
              style={{ backgroundColor: "#25D366" }}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {tr.contact.whatsappBannerCta}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact Info + Form (Light) */}
      <section className="py-24 px-6 bg-slate-50 text-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-16">

          {/* Info Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-1 space-y-10"
          >
            <div>
              <h3 className="text-2xl font-bold mb-6">{tr.contact.infoTitle}</h3>
              <div className="space-y-6">

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{tr.contact.info.email}</p>
                    <a href="mailto:Info@Mediaexperto.com" className="text-base font-medium hover:text-primary transition-colors">
                      Info@Mediaexperto.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{tr.contact.info.phone}</p>
                    <a href="tel:+18097091552" className="text-base font-medium hover:text-primary transition-colors block">
                      (809) 709-1552
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: "#25D366" }}>
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">WhatsApp</p>
                    <a href="https://wa.me/18097091552" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:text-primary transition-colors">
                      +1 (809) 709-1552
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-1">{tr.contact.info.address}</p>
                    <p className="text-base font-medium text-slate-800 leading-relaxed">
                      Av. Presidente Vásquez<br />
                      Santo Domingo Este,<br />
                      República Dominicana
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">{tr.contact.info.social}</h3>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/mediaexperto" target="_blank" rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full bg-[#1877F2] flex items-center justify-center text-white hover:opacity-90 transition-opacity" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/media_experto" target="_blank" rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                  style={{ background: "linear-gradient(135deg, #f58529, #dd2a7b, #515bd4)" }} aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://wa.me/18097091552" target="_blank" rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center text-white hover:opacity-90 transition-opacity" aria-label="WhatsApp">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100"
          >
            {submitted ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{tr.contact.thankYou}</h3>
                <p className="text-slate-600 text-lg">{tr.contact.form.success}</p>
                <Button onClick={() => setSubmitted(false)} variant="outline" className="mt-8">{tr.contact.sendAnother}</Button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">{tr.contact.form.name} *</label>
                    <Input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-50 border-slate-200 focus-visible:ring-primary h-12" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">{tr.contact.form.email} *</label>
                    <Input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className="bg-slate-50 border-slate-200 focus-visible:ring-primary h-12" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">{tr.contact.form.phone}</label>
                    <Input type="tel" placeholder="+1 (809) 000-0000" value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-50 border-slate-200 focus-visible:ring-primary h-12" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">{tr.contact.form.company}</label>
                    <Input value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })}
                      className="bg-slate-50 border-slate-200 focus-visible:ring-primary h-12" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">{tr.contact.form.service}</label>
                  <select
                    value={formData.service}
                    onChange={e => setFormData({ ...formData, service: e.target.value })}
                    className="flex h-12 w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    <option value="">{tr.contact.form.selectService}</option>
                    <option value="web">{tr.contact.form.serviceOptions.web}</option>
                    <option value="design">{tr.contact.form.serviceOptions.design}</option>
                    <option value="marketing">{tr.contact.form.serviceOptions.marketing}</option>
                    <option value="social">{tr.contact.form.serviceOptions.social}</option>
                    <option value="ecommerce">{tr.contact.form.serviceOptions.ecommerce}</option>
                    <option value="apps">{tr.contact.form.serviceOptions.apps}</option>
                    <option value="other">{tr.contact.form.serviceOptions.other}</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">{tr.contact.form.message} *</label>
                  <Textarea required rows={5} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })}
                    className="bg-slate-50 border-slate-200 focus-visible:ring-primary resize-none" />
                </div>

                <Button type="submit" size="lg"
                  className="w-full md:w-auto h-14 px-10 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl transition-all shadow-md">
                  {tr.contact.form.submit}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
